import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardActionArea, Typography, Button, IconButton, Stack, Box, Chip } from '@mui/material';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import { apiClient } from '@/api/axiosClient';
import LabelObjectRenderer from '@/components/labelDesigner/LabelObjectRenderer';
import { SAMPLE_DATA } from '@/components/labelDesigner/LabelCanvas';
import type { ApprovalInstanceDto } from '@/types/approvals';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { LabelTemplate } from '@/types/labelTemplate';

// Read-only card thumbnail — fits the design into a fixed-height box regardless of the template's
// own mm dimensions, capped on width too so a very wide/short label doesn't blow past the card.
const PREVIEW_HEIGHT_PX = 140;
const PREVIEW_MAX_WIDTH_PX = 260;
function previewScale(widthMm: number, heightMm: number): number {
  if (!widthMm || !heightMm) return 1;
  return Math.min(PREVIEW_MAX_WIDTH_PX / widthMm, PREVIEW_HEIGHT_PX / heightMm);
}

export default function LabelTemplateList() {
  const { t } = useTranslation('labelDesigner');
  const { formatDateTime } = useDateFormat();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canAct = hasPermission(PERMISSIONS.APPROVAL_MATRIX_ACT);

  const { data: templates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });
  const submitMutation = useMutation({
    mutationFn: (id: string) => apiClient.post<LabelTemplate>(`/label-templates/${id}/submit`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['label-templates'] }),
  });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/label-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['label-templates'] });
      setConfirmDeleteId(null);
    },
  });
  const templatePendingDelete = templates.find((tpl) => tpl.id === confirmDeleteId) ?? null;
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const assignmentLabel = (tpl: LabelTemplate): string | null =>
    tpl.materialId ? materials.find((m) => m.id === tpl.materialId)?.name ?? null : null;

  // Same GET /approvals/pending query key as NotificationBell.tsx/LabelTemplateDesigner.tsx —
  // shares its cache, so acting from any of the three places stays in sync with the others.
  const { data: pendingApprovals = [] } = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: async () => (await apiClient.get<ApprovalInstanceDto[]>('/approvals/pending')).data,
    enabled: canAct,
  });
  const pendingInstanceFor = (tpl: LabelTemplate) =>
    pendingApprovals.find((i) => i.transactionType === 'LabelTemplate' && i.transactionId === tpl.id);

  const actMutation = useMutation({
    mutationFn: ({ instanceId, decision }: { instanceId: string; decision: 'Approved' | 'Rejected' }) =>
      apiClient.post(`/approvals/${instanceId}/action`, { decision, comments: null }),
    // Refetch on failure too, not just success — a "this approval has already been actioned" error
    // means our cached pending-list/template-status is stale (someone else, or a lost race from a
    // double-click, already resolved it), and without this the card would keep showing
    // PendingApproval with working-looking buttons that fail the exact same way forever.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['label-templates'] });
    },
  });

  // Same synchronous double-click guard used elsewhere in this approval flow —
  // actMutation.isPending flips asynchronously, too slow to stop a real double-click.
  const actInFlight = useRef(false);
  const act = (instanceId: string, decision: 'Approved' | 'Rejected') => {
    if (actInFlight.current) return;
    actInFlight.current = true;
    actMutation.mutate({ instanceId, decision }, { onSettled: () => { actInFlight.current = false; } });
  };

  return (
    <>
      <PageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.LABEL_TEMPLATE_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/label-designer/new')}>
              {t('list.newTemplate')}
            </Button>
          </IfPermitted>
        }
      />

      <GridContainer columns={12}>
        {templates.map((tpl) => (
          <GridItem xs={12} sm={6} md={4} key={tpl.id}>
            <Card>
              <CardActionArea onClick={() => navigate(`/label-designer/${tpl.id}`)}>
                {/* Live thumbnail of the actual designed layout, not a generic placeholder icon —
                    same LabelObjectRenderer the designer canvas itself uses, just scaled down and
                    read-only (no onSelect handed in). Falls back to the icon only for a template
                    with literally nothing on the canvas yet. */}
                <Box sx={{ height: PREVIEW_HEIGHT_PX, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {tpl.objects.length > 0 ? (
                    <Box
                      sx={{
                        position: 'relative',
                        width: tpl.widthMm * previewScale(tpl.widthMm, tpl.heightMm),
                        height: tpl.heightMm * previewScale(tpl.widthMm, tpl.heightMm),
                        bgcolor: '#ffffff',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                        borderRadius: 0.5,
                      }}
                    >
                      {tpl.objects.map((obj) => (
                        <LabelObjectRenderer key={obj.id} object={obj} sampleData={SAMPLE_DATA} scale={previewScale(tpl.widthMm, tpl.heightMm)} />
                      ))}
                    </Box>
                  ) : (
                    <DescriptionOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                  )}
                </Box>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                      {tpl.name}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <StatusChip status={tpl.status} />
                      {/* Hidden once real labels have been printed from this template — its Id is
                          already referenced by print history, so deleting it would leave that
                          history dangling (DeleteAsync refuses the request server-side too). */}
                      {!tpl.hasBeenPrinted && (
                        <IfPermitted permission={PERMISSIONS.LABEL_TEMPLATE_MANAGE}>
                          <IconButton
                            size="small"
                            aria-label={t('list.delete')}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(tpl.id);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        </IfPermitted>
                      )}
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {tpl.description || t('list.noDescription')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={t('list.dimensions', { width: tpl.widthMm, height: tpl.heightMm })} />
                    <Chip size="small" variant="outlined" label={t('list.objectCount', { count: tpl.objects.length })} />
                    {assignmentLabel(tpl) && <Chip size="small" color="primary" variant="outlined" label={t('list.assignedMaterial', { name: assignmentLabel(tpl) })} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    {t('list.updatedBy', { date: formatDateTime(tpl.updatedDate), name: tpl.updatedBy })}
                  </Typography>
                </CardContent>
              </CardActionArea>
              {(tpl.status === 'Draft' || tpl.status === 'Rejected') && (
                <IfPermitted permission={PERMISSIONS.LABEL_TEMPLATE_MANAGE}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<SendOutlinedIcon fontSize="small" />}
                      disabled={submitMutation.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        submitMutation.mutate(tpl.id);
                      }}
                    >
                      {t('list.submitForApproval')}
                    </Button>
                  </Box>
                </IfPermitted>
              )}
              {tpl.status === 'PendingApproval' && pendingInstanceFor(tpl) && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleOutlinedIcon fontSize="small" />}
                      disabled={actMutation.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        act(pendingInstanceFor(tpl)!.id, 'Approved');
                      }}
                    >
                      {t('list.approve')}
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelOutlinedIcon fontSize="small" />}
                      disabled={actMutation.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        act(pendingInstanceFor(tpl)!.id, 'Rejected');
                      }}
                    >
                      {t('list.reject')}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Card>
          </GridItem>
        ))}
      </GridContainer>

      <ConfirmDialog
        open={Boolean(confirmDeleteId)}
        title={t('designer.deleteDialog.title')}
        message={t('designer.deleteDialog.message', { name: templatePendingDelete?.name ?? '' })}
        confirmLabel={t('designer.deleteDialog.confirm')}
        confirmColor="error"
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
        loading={deleteMutation.isPending}
      />
    </>
  );
}
